import React, { useEffect, useRef, useState } from "react";
import CommentForm from "./CommentForm";
import ContextMenu from "./ContextMenu";
import ExpandableTip from "./ExpandableTip";
import HighlightContainer from "./HighlightContainer";
import Sidebar from "./Sidebar";
import Toolbar from "./Toolbar";
import "./style/Toolbar.css";
import * as pdfjsLib from "pdfjs-dist";
import { PdfHighlighter, PdfLoader } from "react-pdf-highlighter-extended";
import "react-toastify/dist/ReactToastify.css";

// Explicitly set worker URL to match the installed react-pdf / pdfjs-dist version
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs`;

const getNextId = () => String(Math.random()).slice(2);

const parseIdFromHash = () => {
  return document.location.hash.slice("#highlight-".length);
};

const resetHash = () => {
  document.location.hash = "";
};

const ViewPdf = ({
  file_url,
  proof_id,
  initial_comments = [],
  proof_status,
  is_view = false,
  onApprove,
  onReject,
  onRefresh,
  onAddComment,
  tabLabel,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [dynamicView, setDynamicView] = useState(is_view);
  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  const [url, setUrl] = useState(file_url);
  const [is_new, setNew] = useState(false);
  const [is_del, setDel] = useState(false);

  const [highlights, setHighlights] = useState(initial_comments || []);
  const [contextMenu, setContextMenu] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [pdfScaleValue, setPdfScaleValue] = useState(undefined);

  const highlighterUtilsRef = useRef();

  useEffect(() => {
    setUrl(file_url);
  }, [file_url]);

  useEffect(() => {
    if (initial_comments) {
      setHighlights((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(initial_comments)) {
          return initial_comments;
        }
        return prev;
      });
    }
  }, [initial_comments]);

  useEffect(() => {
    if (proof_status === "2" || proof_status === "3" || proof_status === "4") {
      setDynamicView(true);
    } else {
      setDynamicView(is_view);
    }
  }, [proof_status, is_view]);

  useEffect(() => {
    const handleClick = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [contextMenu]);

  const handleContextMenu = (event, highlight) => {
    event.preventDefault();

    setContextMenu({
      xPos: event.clientX,
      yPos: event.clientY,
      deleteHighlight: () => deleteComment(highlight.id, highlight),
      editComment: () => editComment(highlight.id, highlight),
      is_view: false,
    });
  };

  const addHighlight = (highlight, comment) => {
    setHighlights([{ ...highlight, comment, id: getNextId() }, ...highlights]);
    setNew(true);
  };

  useEffect(() => {
    if (is_new || is_del) {
      saveHighlights();
    }
  }, [is_new, is_del]);

  const editHighlight = (idToUpdate, edit) => {
    setHighlights(
      highlights.map((highlight) =>
        highlight.id === idToUpdate ? { ...highlight, ...edit } : highlight,
      ),
    );
  };

  const resetHighlights = () => {
    setHighlights([]);
  };

  const saveHighlights = async () => {
    try {
      if (onAddComment) {
        await onAddComment({ proof_id, proof_comments: highlights });
      }

      if (is_new) {
        setNew(false);
        if (onRefresh) onRefresh();
      } else if (is_del) {
        alert("Highlights deleted successfully!");
        setDel(false);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error("Error saving highlights:", error);
      alert("Failed to save highlights. Please try again.");
    }
  };

  const editComment = (_id, highlight) => {
    if (!highlighterUtilsRef.current) return;

    const editCommentTip = {
      position: highlight.position,
      content: (
        <CommentForm
          placeHolder={"Enter Comment"}
          value={highlight.comment}
          onSubmit={(input) => {
            setNew(true);
            editHighlight(highlight.id, { comment: input });
            highlighterUtilsRef.current.setTip(null);
            highlighterUtilsRef.current.toggleEditInProgress(false);
          }}
        />
      ),
    };

    highlighterUtilsRef.current.setTip(editCommentTip);
    highlighterUtilsRef.current.toggleEditInProgress(true);
  };

  const deleteComment = async (_id, highlight) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this comment?",
    );

    if (confirmed) {
      setHighlights(highlights.filter((h) => h.id !== highlight.id));
      setDel(true);
    } else {
      console.log("Deletion cancelled by user");
    }
  };

  const getHighlightById = (id) => {
    return highlights.find((highlight) => highlight.id === id);
  };

  const scrollToHighlightFromHash = React.useCallback(() => {
    const getHighlightById = (id) => {
      return highlights.find((highlight) => highlight.id === id);
    };
    const highlight = getHighlightById(parseIdFromHash());

    if (highlight && highlighterUtilsRef.current) {
      highlighterUtilsRef.current.scrollToHighlight(highlight);
    }
  }, [highlights]);

  useEffect(() => {
    window.addEventListener("hashchange", scrollToHighlightFromHash);

    return () => {
      window.removeEventListener("hashchange", scrollToHighlightFromHash);
    };
  }, [scrollToHighlightFromHash]);

  return (
    <div className="App" style={{ display: "flex", flexDirection: "column" }}>
      {proof_status === "2" ? (
        <h2 style={{ textAlign: "center" }}>
          This Proof has already been Approved.
        </h2>
      ) : proof_status === "3" ? (
        <h2 style={{ textAlign: "center" }}>
          This Proof already Need Changes.
        </h2>
      ) : proof_status === "4" ? (
        <h2 style={{ textAlign: "center" }}>This Proof has been Cancelled.</h2>
      ) : null}

      <div
        className="viewPdf"
        style={{
          display: "flex",
          height: "850px",
          flexDirection: "row-reverse",
        }}
      >
        <Sidebar
          highlights={highlights}
          resetHighlights={resetHighlights}
          isSidebarOpen={isSidebarOpen}
          closeSidebar={() => setIsSidebarOpen(false)}
        />
        <div
          style={{
            height: "850px",
            width: "75vw",
            overflow: "hidden",
            position: "relative",
            flexGrow: 1,
            margin: "1rem",
            borderRadius: "0.6rem",
          }}
        >
          <Toolbar
            setPdfScaleValue={(value) => setPdfScaleValue(value)}
            toggleSidebar={toggleSidebar}
            label={tabLabel}
          />
          {url ? (
            <PdfLoader document={url} key={url}>
              {(pdfDocument) => (
                <PdfHighlighter
                  enableAreaSelection={(_event) => !dynamicView}
                  pdfDocument={pdfDocument}
                  utilsRef={(_pdfHighlighterUtils) => {
                    highlighterUtilsRef.current = _pdfHighlighterUtils;
                  }}
                  pdfScaleValue={pdfScaleValue}
                  selectionTip={
                    !dynamicView ? (
                      <ExpandableTip addHighlight={addHighlight} />
                    ) : undefined
                  }
                  highlights={highlights}
                  onScrollAway={resetHash}
                  style={{
                    height: "calc(100% - 41px)",
                  }}
                >
                  <HighlightContainer
                    editHighlight={editHighlight}
                    editComment={editComment}
                    deleteComment={deleteComment}
                    onContextMenu={handleContextMenu}
                    is_view={dynamicView}
                  />
                </PdfHighlighter>
              )}
            </PdfLoader>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              {error ? (
                <div style={{ textAlign: "center", color: "red" }}>
                  <h3>PDF Not Found</h3>
                  <p>{error}</p>
                </div>
              ) : (
                "Loading PDF..."
              )}
            </div>
          )}
        </div>

        {contextMenu && !is_view && <ContextMenu {...contextMenu} />}
      </div>
    </div>
  );
};

export default ViewPdf;
